<template>
  <!--Bootstrap Classes-->
  <div class="modal show fade" :id="'fixDunningModal' + apparatusId" style="bottom: 25%; display: block;">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <header class="modal-header">
          <div class="row w-100 no-gutters align-items-center">
            <div class="col">
              <h2 data-test-hook="ModalTitle" class="modal-title">
                <i class="fal fa-credit-card mr-1"> </i>
                {{ $t('xYourAccountNeedsAttentionHeader') }}
              </h2>
            </div>
            <div class="col-auto">
              <button
                data-test-hook="CloseModalBtn"
                @click="closeModal()"
                type="button"
                class="btn btn-icon-button--gray btn-icon-button"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </header>
        <div
          v-if="subscriptionLoading"
          class="spinner-border text-warning align-self-center"
          style="width: 5rem; height: 5rem; margin-top: 1em; margin-bottom: 1em;"
          role="status"
        >
          <span class="sr-only">Loading...</span>
        </div>
        <div v-else class="modal-body">
          <div role="alert" v-if="addressSearchError" class="alert alert-danger">
            <h4 class="alert-heading">{{ $t('xSomethingWentWrong') }}</h4>
            <p class="alert-body">{{ $t('xWaitAndTryAgainErrorText') }}</p>
          </div>
          <div>
            <div v-if="!isShowingValidationView">
              <div class="row">
                <div class="col-12">
                  <h3>
                    <strong data-test-hook="CcInfoTitle">{{ $t('xCreditCardInfo') }}</strong>
                    <span class="body-text-color">*</span>
                  </h3>
                </div>
              </div>
              <div class="row mb-2">
                <div class="col">
                  <i class="asterisk-text">{{ $t('xProblemProcessingPayment') }}</i>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <fieldset id="credit-card-number" class="form-group">
                    <div>
                      <input
                        v-model="$v.creditCardNumber.$model"
                        type="text"
                        maxlength="16"
                        v-mask="'################'"
                        :placeholder="$t('xCreditCardNumber')"
                        :state="validState($v.creditCardNumber)"
                        aria-describedby="card-number-invalid"
                        data-test-hook="CcNumber"
                        class="form-control"
                      />
                      <div id="card-number-invalid" class="invalid-feedback">{{ $t('xProblemProcessingPayment') }}</div>
                    </div>
                  </fieldset>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-12 col-md-t6 col-lg-4">
                  <fieldset id="expiry-date-month" class="form-group">
                    <input
                      v-model="$v.expirationMonth.$model"
                      type="text"
                      :placeholder="$t('xMm')"
                      maxlength="2"
                      v-mask="'##'"
                      :state="validState($v.expirationMonth)"
                      aria-describedby="expiry-month-invalid"
                      data-test-hook="CcExpireMonth"
                      class="form-control"
                    />
                    <div id="expiry-month-invalid" class="invalid-feedback">{{ $t('xExpirationMonthValidation') }}</div>
                  </fieldset>
                </div>
                <div class="col-sm-12 col-md-t6 col-lg-4">
                  <fieldset id="expiry-date-year" class="form-group">
                    <input
                      v-model="$v.expirationYear.$model"
                      type="text"
                      :placeholder="$t('xYy')"
                      maxlength="2"
                      v-mask="'##'"
                      :state="validState($v.expirationYear)"
                      aria-describedby="expiry-year-invalid"
                      data-test-hook="CcExpireYear"
                      class="form-control"
                    />
                    <div id="expiry-year-invalid" class="invalid-feedback">{{ $t('xExpirationYearValidation') }}</div>
                  </fieldset>
                </div>
                <div class="col-sm-12 col-md-t6 col-lg-4">
                  <div id="cvv" class="input-group" role="group">
                    <input
                      v-model="$v.cvv.$model"
                      type="text"
                      v-mask="'####'"
                      maxlength="4"
                      :placeholder="$t('xCvv')"
                      :state="validState($v.cvv)"
                      aria-describedby="cvv-invalid"
                      data-test-hook="CcCvv"
                      class="form-control"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">
                        <img :src="require('@/assets/images/3-digit-code.svg')" height="20" />
                      </div>
                    </div>
                    <div id="cvv-invalid" class="invalid-feedback">{{ $t('xExpirationMonthValidation') }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="accountData">
              <div v-if="!isShowingValidationView">
                <div class="row">
                  <div class="col-12">
                    <h3>
                      <strong data-test-hook="BillingInformationTitle">{{ $t('xBillingInformationLowercase') }}</strong>
                    </h3>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <div class="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        class="custom-control-input"
                        v-model="useExistingBillingInformation"
                        id="customCheck"
                        data-test-hook="UseCurrentBillingAddressBtn"
                      />
                      <label class="custom-control-label" for="customCheck" data-test-hook="useCurrBillingInfoText">
                        {{ $t('xUseCurrentBillingInfo') }}
                      </label>
                    </div>
                  </div>
                </div>
                <div class="row" v-if="useExistingBillingInformation">
                  <div data-test-hook="ExistingBillingName" class="col-12">
                    {{ accountProfile.firstName }} {{ accountProfile.lastName }}
                  </div>
                  <div data-test-hook="ExistingBillingAddress" class="col-12">
                    {{ DisplayAddress(accountProfile.address) }}
                  </div>
                </div>
              </div>
              <div v-if="!useExistingBillingInformation">
                <div v-if="!isShowingValidationView">
                  <div class="row mt-2">
                    <div class="col-6">
                      <div role="group" class="form-group" id="firstName-group">
                        <label class="d-block" for="firstName">{{ $t('xFirstNameLowercase') }}</label>
                        <input class="form-control" type="text" id="firstName" v-model="accountData.firstName" />
                      </div>
                    </div>
                    <div class="col-6">
                      <div role="group" class="form-group" id="lastName-group" :title="$t('xLastName')">
                        <label class="d-block" for="lastName">{{ $t('xLastNameLowercase') }}</label>
                        <input class="form-control" type="text" id="lastName" v-model="accountData.lastName" />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <AddressSearchModals
                      ref="updateCardAddressSearchModalsRef"
                      addressSearchModalId="update-card-address-search-form"
                      addressValidationModalId="update-card-address-validation-form"
                      @onSubmit="saveAccountHolder"
                      @addressSearchModalViewChanged="handleValidationStateChange"
                      @setIsNextDisabled="handleSetIsNextDisabled"
                      @addressSearchError="handleAddressSearchError"
                      :inline="true"
                      :showContinueButton="false"
                      :showValidationButtons="false"
                      :showAddressCheckmarkHeader="false"
                      :isBillingAddress="true"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="asterisk-text col">
                <i data-test-hook="UpdatePaymentWarningMsg" class="colorOrange">*{{ $t('xUpdatingPaymentWarning') }}</i>
              </div>
            </div>
          </div>
        </div>
        <footer class="modal-footer">
          <button
            v-if="useExistingBillingInformation"
            variant="warning"
            block
            @click="payForProduct()"
            :disabled="!canPay()"
            data-test-hook="SaveChangesBtn"
            type="button"
            class="btn btn-warning btn-block"
          >
            {{ $t('xSaveAndClose') }}
            <div class="spinner-border" v-if="paying" />
          </button>
          <button
            v-if="!useExistingBillingInformation && !isShowingValidationView"
            variant="outline-warning"
            class="btn btn-outline-warning btn-block"
            @click="addBillingAddress()"
            data-test-hook="SaveChangesBtn"
          >
            {{ $t('xConfirmBillingInfo') }}
            <span v-if="savingAddress">
              <div class="spinner-border ml-2" />
            </span>
          </button>
          <!-- Validation footer buttons -->
          <div
            v-if="!useExistingBillingInformation && isShowingValidationView && !showContactSupportButton"
            class="row w-100 m-0"
          >
            <div class="col pr-1">
              <button
                variant="outline-warning"
                class="btn btn-outline-warning btn-block"
                @click="editAddress"
                data-test-hook="edit-address-button"
              >
                {{ $t('xAddressSearchEditAddress') }}
              </button>
            </div>
            <div class="col pl-1">
              <button
                variant="warning"
                class="btn btn-warning btn-block"
                @click="confirmAddress"
                :disabled="confirmAddressDisabled"
                data-test-hook="confirm-address-button"
              >
                {{ $t('xAddressSearchConfirmAddress') }}
                <span v-if="savingAccountHolder">
                  <div class="spinner-border ml-2" />
                </span>
                <span v-else>
                  <i class="far fa-arrow-right"></i>
                </span>
              </button>
            </div>
          </div>
          <!-- Validation contact support footer buttons -->
          <div
            v-if="!useExistingBillingInformation && isShowingValidationView && showContactSupportButton"
            class="row w-100 m-0"
          >
            <div class="col pr-1">
              <button
                variant="outline-warning"
                class="btn btn-outline-warning btn-block"
                @click="editAddress"
                data-test-hook="edit-address-button"
              >
                {{ $t('xAddressSearchEditAddress') }}
              </button>
            </div>
            <div class="col pl-1">
              <button
                v-if="!useExistingBillingInformation && isShowingValidationView && showContactSupportButton"
                variant="warning"
                class="btn btn-warning btn-block"
                @click="$router.push({ path: `/support` })"
                data-test-hook="contact-support-button"
              >
                {{ $t('xContactSupport') }}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { required, requiredIf, minLength, maxLength, email, numeric } from 'vuelidate/lib/validators'
import * as Constants from '@/constants'
import AddressSearchModals from '@/components/Common/Address/AddressSearchModals'
import { isAddressValid } from '../../utils/address'

const monthInRange = (value, vm) => value >= 1 && value <= 12

export default {
  components: {
    AddressSearchModals
  },
  props: ['existingPaymentInfo', 'accountProfile', 'apparatusId', 'subscriptionStatus'],
  data() {
    return {
      paying: false,
      creditCardNumber: null,
      expirationMonth: null,
      expirationYear: null,
      cvv: null,
      showValidationBanner: false,
      DealerManagedProductId: Constants.DealerManagedProductId,
      FreeProductId: Constants.FreeWifiEthProductId,
      accountData: null,
      useExistingBillingInformation: true,
      errorMessages: [],
      subscription: null,
      subscriptionLoading: false,
      productId: null,
      addressFormDisabled: false,
      isShowingValidationView: false,
      savingAddress: false,
      savingAccountHolder: false,
      addressSearchError: false
    }
  },
  validations: {
    cvv: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(4),
      numeric
    },
    expirationMonth: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric,
      monthInRange
    },
    expirationYear: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric
    },
    creditCardNumber: {
      required,
      minLength: minLength(15),
      maxLength: maxLength(16),
      numeric
    }
  },
  computed: {
    ...mapState({
      currentApparatusDetails: state => state.apparatus.currentApparatusDetails,
      currentAppConfig: state => state.application.currentAppConfig,
      isImpersonating: state => state.auth.isImpersonating
    }),
    confirmAddressDisabled() {
      return !this.$refs.updateCardAddressSearchModalsRef?.selectedAddress
    },
    showContactSupportButton() {
      return this.$refs.updateCardAddressSearchModalsRef?.showContactSupportButton
    }
  },
  methods: {
    async saveAccountHolder(newAddress, responseOptions) {
      this.savingAccountHolder = true
      if (newAddress) {
        const requestBody = {
          firstName: this.accountData.firstName,
          lastName: this.accountData.lastName,
          organizationAddress: {
            type: newAddress.addressType,
            line1: newAddress.address1,
            line2: newAddress.address2,
            city: newAddress.city,
            region: newAddress.state,
            country: newAddress.countryCode,
            postalCode: newAddress.postalCode,
            organizationId: this.accountData.organizationId
          }
        }
        await this.$store
          .dispatch('updateAccountHolder', requestBody)
          .then(async result => {
            if (result) {
              await this.$store.dispatch('getAccountProfile')
              await this.$store.dispatch('getAccountInfo')
              responseOptions.closeAddressModals()
              this.useExistingBillingInformation = true
            } else {
              responseOptions.openAddressFormWithError()
            }
          })
          .catch(error => {
            responseOptions.openAddressFormWithError()
          })
      }
      this.isShowingValidationView = false
      this.savingAccountHolder = false
    },
    async addBillingAddress() {
      this.savingAddress = true
      await this.$refs.updateCardAddressSearchModalsRef.verifyAddress()
      this.savingAddress = false
      if (!this.$refs.updateCardAddressSearchModalsRef.isFormInvalid) this.handleValidationStateChange(true)
    },
    handleValidationStateChange(isShowing) {
      this.isShowingValidationView = isShowing
    },
    editAddress() {
      this.handleValidationStateChange(false)
      this.$refs.updateCardAddressSearchModalsRef.openAddressFormModal()
      this.$refs.updateCardAddressSearchModalsRef.goBackToAddressForm()
    },
    confirmAddress() {
      this.$refs.updateCardAddressSearchModalsRef.saveAndSubmit()
    },
    handleSetIsNextDisabled(isDisabled) {
      this.addressFormDisabled = isDisabled
    },
    handleAddressSearchError(hasError) {
      this.addressSearchError = hasError
    },
    payForProduct() {
      this.paying = true
      let addPaymentModel = {
        organizationId: this.accountProfile.organizationId,
        cardNumber: this.creditCardNumber,
        cvv: this.cvv,
        expirationMonth: this.expirationMonth,
        expirationYear: '20' + this.expirationYear
      }
      this.$store
        .dispatch('addBillingAccountPaymentMethod', addPaymentModel)
        .then(response => {
          document.getElementById('fixDunningModal' + this.apparatusId).style.display = 'none'
          this.$store.dispatch('displayToast', {
            title: this.$t('xSuccess'),
            message: this.$t('xPaymentMethodUpdated'),
            variant: 'success'
          })
          this.$store.dispatch('getApparatuses')
        })
        .catch(error => {
          this.existingPaymentInfo = false
          this.errorMessages = []
          if (error.response.data.errors) {
            if (error.response.data.errors.CardNumber || error.response.data.errors.cardNumber) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xCCIsInvalid'))
            }
            if (error.response.data.errors.Cvv || error.response.data.errors.cvv) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xCvvInvalid'))
            }
            if (error.response.data.errors.ExpirationMonth || error.response.data.errors.expirationMonth) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xExpirationMonthInvalid'))
            }
            if (error.response.data.errors.ExpirationYear || error.response.data.errors.expirationYear) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xExpirationYearInvalid'))
            }
            if (error.response.data.errors.billing || error.response.data.errors.Billing) {
              this.$store.dispatch('displayToast', {
                title: this.$t('xError'),
                message: error.response.data.errors.billing
                  ? error.response.data.errors.billing
                  : error.data.response.errors.Billing,
                variant: 'danger'
              })
            }
          } else {
            this.$store.dispatch('displayToast', {
              title: this.$t('xError'),
              message: this.$t('xFailedToAddPaymentMethod'),
              variant: 'danger'
            })
          }
        })
        .finally(() => {
          this.paying = false
        })
    },
    closeModal() {
      this.useExistingBillingInformation = true
      this.creditCardNumber = null
      this.expirationMonth = null
      this.expirationYear = null
      this.cvv = null
      this.paying = false
      this.showValidationBanner = false
      this.errorMessages = []
      this.$emit('closeGracePeriodModals')
      this.$v.$reset()
      document.getElementById('fixDunningModal' + this.apparatusId).style.display = 'none'
    },
    canPay() {
      if (!this.creditCardNumber || !this.cvv || !this.expirationMonth || !this.expirationYear) {
        return false
      }
      if (!this.$v.$anyError && !this.paying) {
        return true
      } else return false
    }
  },
  watch: {
    accountProfile: function(newVal, oldVal) {
      if (newVal) this.accountData = _.cloneDeep(newVal)
    }
  },
  mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
    }
    this.subscriptionLoading = true
    this.$store
      .dispatch('getApparatusSubscription', {
        organizationId: this.accountProfile.organizationId,
        apparatusId: this.apparatusId
      })
      .then(response => {
        this.subscription = response.data
        if (
          this.subscription.activeProduct &&
          this.subscription.activeProduct.length > 0 &&
          this.subscription.activeProduct[0] &&
          this.subscription.activeProduct[0].productId
        ) {
          this.productId = this.subscription.activeProduct[0].productId
        }
        this.subscriptionLoading = false
      })
      .catch(error => {
        this.subscriptionLoading = false
        this.$store.dispatch('displayToast', {
          title: this.$t('xError'),
          message: this.$t('xNoSubscriptionsFoundForThisGenerator'),
          variant: 'danger'
        })
      })
  }
}
</script>
